import React from 'react'
import { AttachmentLink, Button } from '@pandiafi/design-system'
import { DetailRow } from '@pandiafi/components'
import { useTranslation } from 'react-i18next'

import { FormValues } from './initialValues'
import { Contract, UserMetadata } from '../../../common/validations'
import { useDefectLocations } from './utils'
import { DefectClass } from '../../../common/validations/defectNotice'

import { Section } from 'components'
import { CardTitleBand } from 'components/CardTitleBand/CardTitleBand'

interface DefectNoticePreviewProps {
  contract: Contract
  formValues: FormValues
  isSubmitting: boolean
  submit: () => void
  cancel: () => void
  editMode?: boolean
  userMetadata: UserMetadata
}

export const DefectNoticePreview: React.FC<DefectNoticePreviewProps> = ({
  contract,
  formValues,
  isSubmitting,
  submit,
  cancel,
  editMode,
  userMetadata: { isImpersonator },
}) => {
  const { t } = useTranslation(['common', 'defectNotice'])
  const defectLocations = useDefectLocations(contract)

  const defectLocation = defectLocations.find(
    ({ id }) => id === formValues.defectLocation
  )?.label

  const defectClass = formValues.defectClassId
    ? t(
        `defectNotice:defectClasses.${
          formValues.defectClassId as unknown as DefectClass
        }`
      )
    : t('defectNotice:defectClasses.-1')

  const {
    title,
    description,
    files,
    masterKeyAccessAllowed,
    hasPetsInApartment,
  } = formValues

  const handleCancel = () => {
    cancel()
  }

  const handleSubmit = () => {
    submit()
  }

  return (
    <>
      <CardTitleBand>{t('defectNotice:headings.preview')}</CardTitleBand>
      <Section className="mb-12">
        <DetailRow>
          <DetailRow.Item title={t('defectNotice:labels.defectLocation')}>
            {defectLocation}
          </DetailRow.Item>
          <DetailRow.Item title={t('defectNotice:labels.defectClass')}>
            {defectClass}
          </DetailRow.Item>
          <DetailRow.Item title={t('defectNotice:labels.title')} preLine>
            {title}
          </DetailRow.Item>
          <DetailRow.Item title={t('defectNotice:labels.description')} preLine>
            {description}
          </DetailRow.Item>
          <DetailRow.Item title={t('defectNotice:labels.files')}>
            {files.length
              ? files.map((file) => (
                  <AttachmentLink
                    key={file.fileName}
                    href={file._objectUrl}
                    text={file.metadata?.originalFileName ?? file.fileName}
                    icon={file.fileType === 'image' ? 'image' : 'file'}
                    target="_blank"
                    className="mb-3 last:mb-0 break-all"
                  />
                ))
              : '-'}
          </DetailRow.Item>
          <DetailRow.Item
            title={t('defectNotice:labels.masterKeyAccessAllowed')}
          >
            {masterKeyAccessAllowed ? t('common:yes') : t('common:no')}
          </DetailRow.Item>
          <DetailRow.Item title={t('defectNotice:labels.hasPetsInApartment')}>
            {hasPetsInApartment ? t('common:yes') : t('common:no')}
          </DetailRow.Item>
        </DetailRow>
      </Section>
      <div className="mt-16 flex items-center justify-end">
        <Button type="button" variant="cancel" onClick={() => handleCancel()}>
          {t('defectNotice:actions.back')}
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => handleSubmit()}
          disabled={isImpersonator || isSubmitting}
          loading={isSubmitting}
        >
          {editMode ? t('common:save') : t('defectNotice:actions.sendForm')}
        </Button>
      </div>
    </>
  )
}
